import { useModal } from "@/app/shared/modal-views/use-modal";
import UserCog from "@/components/icons/user-cog";
import { Role, User } from "@/types";
import cn from "@/utils/class-names";
import {
    PiMagnifyingGlass,
    PiTrash,
    PiUserMinus,
    PiUserPlus,
    PiUsers,
    PiXBold,
} from "react-icons/pi";
import { ActionIcon, Button, Input, Modal, Title } from "rizzui";
import { PiFolderLockDuotone } from "react-icons/pi";
import AssignRole from "@/app/shared/roles-permissions/assign-role";
import {
    deleteRole,
    fetchUsersByRole,
    removeRole,
} from "@/server/api-services";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import routes from "@/routers/routes";
import { permissionsArray } from "./permissions";
import toast from "react-hot-toast";
import GenericModal from "@/components/common/GenericModal";

interface RoleCardProps {
    id: string;
    name: string;
    permission?: string[];
    color?: string;
    className?: string;
    icon?: React.ReactNode;
    users: User[];
    roles: Role[];
    refreshRoles: () => void;
}

export default function RoleCard({
    id: Id,
    name,
    permission,
    color,
    users,
    roles,
    className,
    refreshRoles,
}: RoleCardProps) {
    const { openModal } = useModal();
    const [usersByRole, setUsersByRole] = useState<User[]>([]);
    const [loadingUsersByRole, setLoadingUsersByRole] = useState(false);
    const [searchAssignedUser, setSearchAssignedUser] = useState("");
    const [isRemovingUserId, setIsRemovingUserId] = useState<string | null>(
        null,
    );
    const [userPendingRemoval, setUserPendingRemoval] = useState<User | null>(
        null,
    );

    useEffect(() => {
        handleFetchUsersByRole();
    }, []);

    const handleFetchUsersByRole = async () => {
        setLoadingUsersByRole(true);
        const response = await fetchUsersByRole(Id);

        if (response.statusCode !== 200) {
            setLoadingUsersByRole(false);
            toast.error("Failed to fetch assigned users.");
            return;
        }

        const roleUsers = (response.data as User[]) || [];

        setUsersByRole(roleUsers);
        setLoadingUsersByRole(false);
    };

    const availableUsers = useMemo(
        () =>
            users.filter(
                (user) => !usersByRole.some((assigned) => assigned.id === user.id),
            ),
        [users, usersByRole],
    );

    const filteredAssignedUsers = useMemo(() => {
        const query = searchAssignedUser.trim().toLowerCase();

        if (!query) {
            return usersByRole;
        }

        return usersByRole.filter(
            (user) =>
                user.fullname.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query),
        );
    }, [searchAssignedUser, usersByRole]);

    // delete role
    const handleDeleteRole = async () => {
        const response = await deleteRole(Id);
        if (response.statusCode !== 200) {
            toast.error("Failed to delete role");
            return;
        }
        toast.success("Role deleted.");
        refreshRoles();
    };

    const handleUnassignRole = async () => {
        if (!userPendingRemoval) {
            return;
        }

        setIsRemovingUserId(userPendingRemoval.id);
        const response = await removeRole(userPendingRemoval.id, Id);

        if (response.statusCode !== 200) {
            setIsRemovingUserId(null);
            toast.error(`Failed to unassign ${userPendingRemoval.fullname}.`);
            return;
        }

        toast.success(`${userPendingRemoval.fullname} was unassigned.`);
        setUserPendingRemoval(null);
        await handleFetchUsersByRole();
        refreshRoles();
        setIsRemovingUserId(null);
    };

    return (
        <div className={cn("rounded-lg border border-muted p-6", className)}>
            <header className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <span
                        className="grid h-10 w-10 place-content-center rounded-lg text-white"
                        style={{
                            backgroundColor: color,
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M7 6.5H16.75C18.8567 6.5 19.91 6.5 20.6667 7.00559C20.9943 7.22447 21.2755 7.50572 21.4944 7.83329C21.935 8.49268 21.9916 8.96506 21.9989 10.5M12 6.5L11.3666 5.23313C10.8418 4.18358 10.3622 3.12712 9.19926 2.69101C8.6899 2.5 8.10802 2.5 6.94427 2.5C5.1278 2.5 4.21956 2.5 3.53806 2.88032C3.05227 3.15142 2.65142 3.55227 2.38032 4.03806C2 4.71956 2 5.6278 2 7.44427V10.5C2 15.214 2 17.5711 3.46447 19.0355C4.8215 20.3926 6.44493 20.4927 10.5 20.5H11"
                                stroke="currentColor"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                            />
                            <path
                                d="M15.59 18.9736C14.9612 19.3001 13.3126 19.9668 14.3167 20.801C14.8072 21.2085 15.3536 21.4999 16.0404 21.4999H19.9596C20.6464 21.4999 21.1928 21.2085 21.6833 20.801C22.6874 19.9668 21.0388 19.3001 20.41 18.9736C18.9355 18.208 17.0645 18.208 15.59 18.9736Z"
                                stroke="currentColor"
                                strokeWidth="1.3"
                            />
                            <path
                                d="M20 14.4378C20 15.508 19.1046 16.3756 18 16.3756C16.8954 16.3756 16 15.508 16 14.4378C16 13.3676 16.8954 12.5 18 12.5C19.1046 12.5 20 13.3676 20 14.4378Z"
                                stroke="currentColor"
                                strokeWidth="1.3"
                            />
                        </svg>
                    </span>
                    <div className="flex items-end justify-between">
                        <Title as="h4" className="font-medium">
                            {name}
                        </Title>
                    </div>
                </div>
                <GenericModal
                    title="Delete Role"
                    icon={<PiTrash className="h-5 w-5" />}
                    description="Are you sure you want to delete this role?"
                    onConfirm={handleDeleteRole}
                    color="danger"
                    buttonType="flat"
                />
            </header>

            <div className="mt-4 flex items-center gap-2">
                <div className="flex items-center gap-2">
                    <PiUsers className="h-5 w-5" />
                    <span className="text-gray-500 me-3">users:</span>
                    <span className="text-gray-500">
                        {usersByRole.length} / {users.length}
                    </span>
                </div>
                <div className="flex items-center ml-3">
                    <PiFolderLockDuotone className="h-4 w-4 me-3" />
                    <span className="text-gray-500 me-3">Permissions:</span>
                    <span className="text-gray-500">
                        {permission?.length} / {permissionsArray.length}
                    </span>
                </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
                <Link
                    to={routes.userManagement.editRole(Id)}
                    className="flex items-center gap-1 text-gray-800 @lg:w-full lg:mt-6 lg:justify-center lg:gap-2 lg:px-4 lg:py-2 lg:bg-gray-100 lg:rounded-lg lg:text-sm lg:font-medium lg:justify-center lg:w-full lg:mt-6"
                >
                    <UserCog className="h-4 w-4" />
                    Permissions
                </Link>
                <Button
                    variant="flat"
                    className="flex items-center gap-1 text-gray-800 @lg:w-full lg:mt-6 lg:justify-center lg:gap-2 lg:px-4 lg:py-2 lg:bg-gray-100 lg:rounded-lg lg:text-sm lg:font-medium lg:justify-center lg:w-full lg:mt-6"
                    disabled={availableUsers.length === 0}
                    onClick={() =>
                        openModal({
                            view: (
                                <AssignRole
                                    users={availableUsers}
                                    roleId={Id}
                                    onAssigned={async () => {
                                        await handleFetchUsersByRole();
                                        refreshRoles();
                                    }}
                                />
                            ),
                        })
                    }
                >
                    <PiUserPlus className="h-4 w-4" />
                    Assign Role
                </Button>
            </div>

            <div className="mt-5 rounded-lg border border-muted p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                    <Title as="h6" className="text-sm font-semibold">
                        Assigned Users
                    </Title>
                    <span className="text-xs text-gray-500">
                        {usersByRole.length} assigned
                    </span>
                </div>

                <Input
                    type="search"
                    value={searchAssignedUser}
                    onChange={(event) =>
                        setSearchAssignedUser(event.target.value)
                    }
                    placeholder="Search assigned users"
                    prefix={<PiMagnifyingGlass className="h-4 w-4" />}
                    className="mb-3"
                />

                <div className="max-h-52 space-y-2 overflow-y-auto">
                    {loadingUsersByRole ? (
                        <p className="text-sm text-gray-500">Loading users...</p>
                    ) : filteredAssignedUsers.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            {usersByRole.length === 0
                                ? "No users assigned yet."
                                : "No assigned users match your search."}
                        </p>
                    ) : (
                        filteredAssignedUsers.map((user) => (
                            <div
                                key={user.id}
                                className="flex items-center justify-between rounded-md bg-gray-50 px-2 py-2"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-900">
                                        {user.fullname}
                                    </p>
                                    <p className="truncate text-xs text-gray-500">
                                        {user.email}
                                    </p>
                                </div>
                                <ActionIcon
                                    size="sm"
                                    variant="text"
                                    color="danger"
                                    onClick={() => setUserPendingRemoval(user)}
                                    isLoading={isRemovingUserId === user.id}
                                    aria-label={`Unassign ${user.fullname}`}
                                >
                                    <PiUserMinus className="h-4 w-4" />
                                </ActionIcon>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <Modal
                isOpen={Boolean(userPendingRemoval)}
                onClose={() => setUserPendingRemoval(null)}
            >
                <div className="m-auto px-7 pt-6 pb-8">
                    <div className="mb-7 flex items-center justify-between">
                        <div>
                            <Title as="h5" className="font-semibold">
                                Unassign User
                            </Title>
                            <p className="text-sm text-gray-500">
                                Remove {userPendingRemoval?.fullname} from this role?
                            </p>
                        </div>
                        <ActionIcon
                            size="sm"
                            variant="text"
                            onClick={() => setUserPendingRemoval(null)}
                        >
                            <PiXBold className="h-5 w-5" />
                        </ActionIcon>
                    </div>

                    <div className="flex justify-end gap-4">
                        <Button
                            variant="outline"
                            onClick={() => setUserPendingRemoval(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="flat"
                            color="danger"
                            onClick={handleUnassignRole}
                            isLoading={Boolean(isRemovingUserId)}
                        >
                            Unassign
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
